import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";
import { Newspaper, Play, Pause, Trophy, Radio, Tag, Users, Sparkles, Flame, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => new Intl.NumberFormat("fr-CH").format(n || 0);
const MEDALS = ["text-gold", "text-cream", "text-[#c98a4b]"];

/* petites barres d'égaliseur animées — extrait en cours */
const EqBars = ({ className = "text-gold" }) => (
    <span className={`flex items-end gap-[3px] ${className}`} aria-hidden>
        {[0, 1, 2].map((i) => (
            <motion.span
                key={i}
                className="w-[3px] rounded-full bg-current"
                animate={{ height: [4, 14, 6, 16, 5] }}
                transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: "easeInOut" }}
            />
        ))}
    </span>
);

// ---------- tabloïd card : extrait audio + dernier clip YouTube ----------
function ArtistCard({ a, rank, lang }) {
    const [playing, setPlaying] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const audioRef = useRef(null);
    const it = a.itunes || {};
    const video = a.latest_video || {};
    const cover = it.cover || video.thumbnail;

    const togglePreview = () => {
        if (!it.preview) return;
        if (!audioRef.current) {
            audioRef.current = new Audio(it.preview);
            audioRef.current.onended = () => setPlaying(false);
        }
        if (playing) audioRef.current.pause();
        else audioRef.current.play().catch(() => {});
        setPlaying(!playing);
    };
    useEffect(() => () => audioRef.current?.pause(), []);

    return (
        <motion.article
            data-testid={`chart-card-${rank}`}
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: rank * 0.06, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -6 }}
            className="group relative border border-white/10 bg-ink/60 backdrop-blur-sm transition-colors duration-300 hover:border-gold/50"
        >
            {/* rank — entrée animée */}
            <div className="absolute -left-1 -top-5 z-10 overflow-hidden">
                <motion.span
                    initial={{ y: "110%" }}
                    whileInView={{ y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: rank * 0.06 + 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className={`block font-display text-6xl font-extrabold tracking-tighter md:text-7xl ${MEDALS[rank] || "text-cream/40"}`}
                >
                    {String(rank + 1).padStart(2, "0")}
                </motion.span>
            </div>

            <div className="relative mt-8 aspect-square overflow-hidden">
                {showVideo && video.id ? (
                    <iframe
                        src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
                        title={video.title}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        className="h-full w-full"
                    />
                ) : (
                    <>
                        {cover ? (
                            <img src={cover} alt={a.artist} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:rotate-1 group-hover:scale-105" loading="lazy" />
                        ) : (
                            <div className="flex h-full items-center justify-center bg-white/5">
                                <Users size={34} className="text-cream/20" />
                            </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
                        {/* reflet balayant au hover */}
                        <div className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                        {playing && (
                            <span className="absolute left-3 top-3 border border-gold/60 bg-ink/80 px-2.5 py-1.5 backdrop-blur-sm">
                                <EqBars />
                            </span>
                        )}
                        {video.id && (
                            <button
                                data-testid={`chart-video-${rank}`}
                                onClick={() => setShowVideo(true)}
                                aria-label={tx(lang, T.culture.watch)}
                                className="absolute bottom-3 right-3 flex h-11 w-11 items-center justify-center rounded-full border border-violet/70 bg-ink/70 text-violet transition-all hover:scale-110 hover:bg-violet hover:text-cream"
                            >
                                <span className="absolute inset-0 animate-pulse-ring rounded-full border border-violet/60" />
                                <Play size={16} className="ml-0.5" />
                            </button>
                        )}
                        {it.preview && (
                            <button
                                data-testid={`chart-preview-${rank}`}
                                onClick={togglePreview}
                                aria-label={tx(lang, T.culture.excerpt)}
                                className={`absolute bottom-3 left-3 flex h-11 w-11 items-center justify-center rounded-full border transition-all hover:scale-110 ${playing ? "border-gold bg-gold text-ink" : "border-gold/70 bg-ink/70 text-gold hover:bg-gold hover:text-ink"}`}
                            >
                                {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                            </button>
                        )}
                    </>
                )}
            </div>

            <div className="p-4">
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-cream">{a.artist}</h3>
                {video.title && (
                    <p className="mt-1 truncate font-mono text-[9px] tracking-[0.15em] text-cream/50">
                        {video.title}
                    </p>
                )}
                <div className="mt-3 flex items-center justify-between font-mono text-[9px] tracking-[0.2em]">
                    <span className="text-gold/90">
                        <Flame size={10} className="mr-1 inline" />
                        {fmt(a.score)} {tx(lang, T.culture.views)}
                    </span>
                    {video.published && <span className="text-cream/35">{video.published}</span>}
                </div>
            </div>
        </motion.article>
    );
}

/* bouton partage Top 10 — confirmation animée au téléchargement */
function ShareTop10Button({ lang }) {
    const [done, setDone] = useState(false);
    const onClick = () => {
        setDone(true);
        setTimeout(() => setDone(false), 2600);
    };
    return (
        <span className="relative inline-flex">
            <a
                data-testid="chart-share-btn"
                href={`${API}/api/chart/share.png`}
                download="top10-ch.png"
                onClick={onClick}
                className={`relative inline-flex items-center gap-2 overflow-hidden border px-5 py-2.5 font-mono text-[9px] tracking-[0.25em] transition-all duration-300 ${
                    done ? "border-green-500/70 bg-green-500/10 text-green-400" : "border-gold/60 text-gold hover:bg-gold hover:text-ink"
                }`}
            >
                <AnimatePresence mode="wait" initial={false}>
                    {done ? (
                        <motion.span
                            key="done"
                            initial={{ y: 14, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -14, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="flex items-center gap-2"
                        >
                            <motion.svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <motion.path
                                    d="M20 6L9 17l-5-5"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 0.4, ease: "easeOut" }}
                                />
                            </motion.svg>
                            {lang === "fr" ? "IMAGE TÉLÉCHARGÉE" : "IMAGE DOWNLOADED"}
                        </motion.span>
                    ) : (
                        <motion.span
                            key="idle"
                            initial={{ y: 14, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -14, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="flex items-center gap-2"
                        >
                            <Flame size={12} /> {tx(lang, T.culture.share)}
                        </motion.span>
                    )}
                </AnimatePresence>
            </a>
            {/* particules de confirmation */}
            {done && (
                <span className="pointer-events-none absolute inset-0">
                    {[...Array(6)].map((_, i) => (
                        <motion.span
                            key={i}
                            initial={{ opacity: 1, x: "50%", y: "50%", scale: 0 }}
                            animate={{
                                opacity: 0,
                                x: `${50 + (i % 2 ? 1 : -1) * (30 + i * 12)}%`,
                                y: `${-40 - i * 8}%`,
                                scale: 1,
                            }}
                            transition={{ duration: 0.9, delay: i * 0.05, ease: "easeOut" }}
                            className="absolute h-1.5 w-1.5 rounded-full bg-gold"
                            style={{ left: 0, top: "100%" }}
                        />
                    ))}
                </span>
            )}
        </span>
    );
}

function ArticleCard({ a, i, lang }) {
    const [open, setOpen] = useState(false);
    return (
        <motion.article
            data-testid={`article-${i}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -4 }}
            className="group relative overflow-hidden border border-white/10 bg-ink/60 transition-colors duration-300 hover:border-violet/40"
        >
            {/* liseré éditorial violet */}
            <span className="absolute left-0 top-0 h-full w-[3px] origin-top scale-y-0 bg-violet transition-transform duration-300 group-hover:scale-y-100" />
            {a.video_id && (
                <div className="aspect-video w-full">
                    <iframe
                        src={`https://www.youtube.com/embed/${a.video_id}?rel=0`}
                        title={a.title}
                        allow="encrypted-media"
                        allowFullScreen
                        loading="lazy"
                        className="h-full w-full"
                    />
                </div>
            )}
            <div className="p-6">
                <p className="font-mono text-[9px] tracking-[0.3em] text-violet">{a.artist} — {a.published_video}</p>
                <h3 className="mt-2 font-display text-2xl font-bold uppercase leading-tight tracking-tight text-cream transition-colors duration-300 group-hover:text-white">{a.title}</h3>
                <motion.div
                    animate={{ height: "auto" }}
                    className="overflow-hidden"
                >
                    <p className={`mt-3 whitespace-pre-line text-sm leading-relaxed text-cream/70 transition-all duration-500 ${open ? "" : "line-clamp-4"}`}>{a.body}</p>
                </motion.div>
                <button
                    data-testid={`article-more-${i}`}
                    onClick={() => setOpen(!open)}
                    className="group/more mt-4 inline-flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] text-gold transition-colors hover:text-cream"
                >
                    {open ? tx(lang, T.culture.less) : tx(lang, T.culture.more)}
                    <span className={`inline-block transition-transform duration-300 ${open ? "rotate-180" : "group-hover/more:translate-y-0.5"}`}>↓</span>
                </button>
            </div>
        </motion.article>
    );
}

export default function Culture() {
    const { lang } = useLang();
    const [tab, setTab] = useState(() => window.__cultureTab || "culture");
    const go = (id) => window.__goPage?.(id);
    const [chart, setChart] = useState([]);
    const [refresh, setRefresh] = useState(null);
    const [articles, setArticles] = useState([]);

    useEffect(() => {
        const onTab = (e) => setTab(e.detail || "culture");
        window.addEventListener("culture-tab", onTab);
        return () => window.removeEventListener("culture-tab", onTab);
    }, []);

    useEffect(() => {
        fetch(`${API}/api/chart`).then((r) => r.json()).then((d) => {
            setChart(d.artists || []);
            setRefresh(d.last_refresh);
        }).catch(() => {});
        fetch(`${API}/api/articles`).then((r) => r.json()).then((d) => setArticles(d.articles || [])).catch(() => {});
    }, []);

    const tabs = [
        { k: "culture", label: tx(lang, T.culture.tabCulture), icon: Sparkles },
        { k: "chart", label: tx(lang, T.culture.tabChart), icon: Trophy },
        { k: "articles", label: tx(lang, T.culture.tabArticles), icon: Newspaper },
    ];

    return (
        <section data-testid="culture-page" className="relative min-h-screen overflow-hidden bg-ink pb-24 text-cream">
            <div className="pointer-events-none absolute inset-0 opacity-20"
                style={{ background: "radial-gradient(50% 40% at 20% 0%, rgba(138,43,226,0.25) 0%, transparent 70%), radial-gradient(40% 35% at 85% 30%, rgba(212,175,55,0.14) 0%, transparent 70%)" }} />
            {/* bandeau défilant — identité culture suisse */}
            <div className="relative mt-[72px] overflow-hidden border-y border-white/10 bg-ink/80 py-2.5 backdrop-blur-md">
                <div className="animate-marquee flex w-max whitespace-nowrap">
                    {[0, 1].map((k) => (
                        <span key={k} className="flex items-center font-mono text-[10px] tracking-[0.4em] text-violet/80">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <span key={i} className="flex items-center">
                                    <span className="mx-6">{lang === "fr" ? "CULTURE HIP-HOP SUISSE" : "SWISS HIP-HOP CULTURE"}</span>
                                    <span className="text-gold">•</span>
                                    <span className="mx-6">TOP 10 CH</span>
                                    <span className="text-gold">•</span>
                                    <span className="mx-6">GENÈVE</span>
                                    <span className="text-gold">•</span>
                                    <span className="mx-6">LAUSANNE</span>
                                    <span className="text-gold">•</span>
                                    <span className="mx-6">NEUCHÂTEL</span>
                                    <span className="text-gold">•</span>
                                    <span className="mx-6">FRIBOURG</span>
                                    <span className="text-gold">•</span>
                                    <span className="mx-6">ZURICH</span>
                                    <span className="text-gold">•</span>
                                    <span className="mx-6">LA CHAUX-DE-FONDS</span>
                                    <span className="text-gold">•</span>
                                    <span className="mx-6">BERN</span>
                                    <span className="text-gold">•</span>
                                    <span className="mx-6">BIENNE</span>
                                    <span className="text-gold">•</span>
                                </span>
                            ))}
                        </span>
                    ))}
                </div>
            </div>
            <div className="relative mx-auto max-w-[1200px] px-5 pt-16 md:px-10">
                <SectionLabel en={tx(lang, T.culture.label)} fr={tx(lang, T.culture.label)} />
                <FadeUp delay={0.1}>
                    <h1 className="mt-8 font-display text-[clamp(2.4rem,7vw,6rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                        {tx(lang, T.culture.title)}<span className="text-violet">.</span>
                    </h1>
                </FadeUp>

                {/* tabs + tarifs */}
                <FadeUp delay={0.15}>
                    <div className="mt-10 flex flex-wrap items-center gap-3">
                        {tabs.map((t) => (
                            <button
                                key={t.k}
                                data-testid={`culture-tab-${t.k}`}
                                onClick={() => setTab(t.k)}
                                className={`relative flex items-center gap-2 border px-5 py-2.5 font-mono text-[10px] tracking-[0.25em] transition-colors duration-300 ${tab === t.k ? "border-violet text-cream" : "border-white/15 text-cream/60 hover:border-violet hover:text-violet"}`}
                            >
                                {tab === t.k && (
                                    <motion.span
                                        layoutId="culture-tab-pill"
                                        className="absolute inset-0 bg-violet"
                                        transition={{ type: "spring", stiffness: 400, damping: 32 }}
                                    />
                                )}
                                <span className="relative flex items-center gap-2">
                                    <t.icon size={12} /> {t.label}
                                </span>
                            </button>
                        ))}
                        <button
                            data-testid="culture-tarifs-btn"
                            onClick={() => {
                                go("contact");
                                setTimeout(() => scrollToId("#estimator", -70), 1300);
                                setTimeout(() => scrollToId("#estimator", -70), 2200);
                            }}
                            className="ml-auto flex items-center gap-2 border border-gold/60 px-5 py-2.5 font-mono text-[10px] tracking-[0.25em] text-gold transition-all hover:bg-gold hover:text-ink"
                        >
                            <Tag size={12} /> {tx(lang, T.culture.tarifs)}
                        </button>
                    </div>
                </FadeUp>

                {/* CULTURE */}
                {tab === "culture" && (
                    <div className="mt-12">
                        <FadeUp delay={0.1}>
                            <p className="max-w-2xl text-base leading-relaxed text-cream/70 md:text-lg">{tx(lang, T.culture.intro)}</p>
                        </FadeUp>
                        <div className="mt-12 grid gap-6 md:grid-cols-3">
                            {T.culture.points.map((p, i) => (
                                <FadeUp key={i} delay={0.15 + i * 0.1}>
                                    <motion.div
                                        data-testid={`culture-point-${i}`}
                                        whileHover={{ y: -5 }}
                                        transition={{ duration: 0.3 }}
                                        className="group h-full border border-white/10 bg-white/[0.03] p-7 transition-colors duration-300 hover:border-violet/50"
                                    >
                                        <p className="font-mono text-[10px] tracking-[0.35em] text-violet transition-colors group-hover:text-gold">{String(i + 1).padStart(2, "0")}</p>
                                        <h3 className="mt-3 font-display text-lg font-bold uppercase tracking-tight text-cream">{tx(lang, p.title)}</h3>
                                        <p className="mt-3 text-sm leading-relaxed text-cream/60">{tx(lang, p.body)}</p>
                                    </motion.div>
                                </FadeUp>
                            ))}
                        </div>
                        <FadeUp delay={0.3}>
                            <button
                                data-testid="culture-radio-btn"
                                onClick={() => go("radio")}
                                className="mt-12 inline-flex items-center gap-3 border border-violet/60 px-7 py-3.5 font-mono text-[10px] tracking-[0.3em] text-violet transition-all hover:bg-violet hover:text-cream"
                            >
                                <Radio size={13} /> GLM RADIO
                            </button>
                        </FadeUp>
                    </div>
                )}

                {/* TOP 10 CH */}
                {tab === "chart" && (
                    <div className="mt-12">
                        <FadeUp delay={0.1}>
                            <div className="flex flex-wrap items-end justify-between gap-4">
                                <div>
                                    <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight md:text-4xl">
                                        {tx(lang, T.culture.chartTitle)}<span className="text-gold">.</span>
                                    </h2>
                                    <p className="mt-2 max-w-xl font-mono text-[9px] leading-relaxed tracking-[0.25em] text-cream/45">{tx(lang, T.culture.chartSub)}</p>
                                </div>
                                {refresh && (
                                    <p data-testid="chart-refresh" className="font-mono text-[8px] tracking-[0.25em] text-cream/30">
                                        {tx(lang, T.culture.updated)} {String(refresh).slice(0, 10)}
                                    </p>
                                )}
                                <ShareTop10Button lang={lang} />
                            </div>
                        </FadeUp>
                        <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {chart.map((a, i) => <ArtistCard key={a.slug} a={a} rank={i} lang={lang} />)}
                        </div>
                        {chart.length === 0 && (
                            <p className="mt-16 text-center font-mono text-[10px] tracking-[0.3em] text-cream/40">{tx(lang, T.culture.loading)}</p>
                        )}
                    </div>
                )}

                {/* ARTICLES */}
                {tab === "articles" && (
                    <div className="mt-12">
                        <FadeUp delay={0.1}>
                            <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight md:text-4xl">
                                {tx(lang, T.culture.articlesTitle)}<span className="text-violet">.</span>
                            </h2>
                            <p className="mt-2 max-w-xl font-mono text-[9px] leading-relaxed tracking-[0.25em] text-cream/45">{tx(lang, T.culture.articlesSub)}</p>
                        </FadeUp>
                        <div className="mt-12 grid gap-8 md:grid-cols-2 xl:grid-cols-3">
                            {articles.map((a, i) => <ArticleCard key={a.article_id} a={a} i={i} lang={lang} />)}
                        </div>
                        {articles.length === 0 && (
                            <p data-testid="articles-empty" className="mt-16 text-center font-mono text-[10px] tracking-[0.3em] text-cream/40">{tx(lang, T.culture.articlesEmpty)}</p>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
